import React, { useState, useEffect } from 'react';
import { Shield, Star, Phone, AlertCircle, Loader } from 'lucide-react';
import { bailAgentService } from '../../../services/bailAgentService';

export default function TeamMemberSelection({ selectedAgent, onSelectAgent }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('Fetching team members from Supabase...');

      // CRITICAL FIX: Use dedicated scheduling method that bypasses public RLS restrictions
      const { data, error: fetchError } = await bailAgentService?.getAllAgentsForScheduling();

      if (fetchError) {
        console.error('Supabase fetch error:', fetchError);
        throw new Error(fetchError);
      }

      // MANDATORY LOGGING - DO NOT REMOVE
      console.log('DATA FETCHED FROM SUPABASE:', data);
      console.log(`📊 Total team members retrieved: ${data?.length || 0}`);

      if (!data || data?.length === 0) {
        console.warn('⚠️ WARNING: No team members returned from database');
        setTeamMembers([]);
        return;
      }

      // Map database columns to display format with role assignment
      const membersWithRoles = data?.map(member => ({
        ...member,
        // Assign roles based on name for proper display
        title: member?.full_name === 'Michael Sailors' ? 'Supervising Investigator' :
               member?.full_name === 'Sarah Bane' ? 'Office Administrator' : 'Bail Agent'
      })) || [];

      console.log('✅ Team members to display:', membersWithRoles?.map(m => m?.full_name));

      setTeamMembers(membersWithRoles);
    } catch (err) {
      console.error('❌ Error fetching team members:', err);
      setError('Failed to load team member information. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600">Loading team members...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-red-800 font-semibold mb-2">Error Loading Team</h3>
            <p className="text-sm text-red-700 mb-4">{error}</p>
            <button
              onClick={fetchTeamMembers}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (teamMembers?.length === 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-yellow-800 font-semibold mb-2">No Team Members Available</h3>
            <p className="text-sm text-yellow-700">
              Please contact us at{' '}
              <a href="tel:+19162563468" className="font-semibold underline">
                (916) 256-3468
              </a>
              {' '}to schedule an appointment.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">
        Select Your Team Member
      </h2>
      <p className="text-gray-600 mb-8">
        Choose from one of our team members, who are ready to assist you with professional service and expertise.
      </p>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {teamMembers?.map((member) => (
          <button
            key={member?.id}
            onClick={() => onSelectAgent(member)}
            className={`text-left p-6 rounded-xl border-2 transition-all hover:shadow-lg ${
              selectedAgent?.id === member?.id
                ? 'border-blue-600 bg-blue-50 shadow-lg'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            {/* Team Member Photo */}
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white text-xl font-bold">
                {member?.full_name?.split(' ')?.map(n => n?.[0])?.join('')}
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg text-gray-900">{member?.full_name}</h3>
                <p className="text-sm font-medium text-blue-600">{member?.title}</p>
                <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
                  <Star className="w-3 h-3 text-yellow-500 fill-current" />
                  <span>{member?.years_experience} years exp.</span>
                </div>
              </div>
            </div>

            {/* Bio */}
            {member?.bio && (
              <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                {member?.bio}
              </p>
            )}

            {/* Specializations */}
            {member?.specializations && member?.specializations?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {member?.specializations?.slice(0, 2)?.map((spec, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full"
                  >
                    {spec}
                  </span>
                ))}
                {member?.specializations?.length > 2 && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                    +{member?.specializations?.length - 2} more
                  </span>
                )}
              </div>
            )}

            {/* Contact Information */}
            <div className="space-y-2 pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Phone className="w-4 h-4 text-blue-600" />
                <span className="font-medium">{member?.phone}</span>
              </div>
            </div>

            {/* Selected Indicator */}
            {selectedAgent?.id === member?.id && (
              <div className="mt-4 flex items-center gap-2 text-blue-600 font-semibold">
                <Shield className="w-5 h-5" />
                <span>Selected</span>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}